import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { RequireStaff } from "@/components/RequireStaff";
import { SecureDealBox } from "@/components/deal-box";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { FolderOpen, Shield } from "lucide-react";

interface Deal { id: string; business_name: string; current_stage: string; }

export default function StaffDocuments() {
  const { language } = useLanguage();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const t = language === "fr"
    ? { title: "Documents des Deals", subtitle: "Accedez et gerez les documents de tous les deals", selectDeal: "Selectionner un deal", noDeals: "Aucun deal trouve.", staffAccess: "Acces Staff" }
    : { title: "Deal Documents", subtitle: "Access and manage documents for all deals", selectDeal: "Select a deal", noDeals: "No deals found.", staffAccess: "Staff Access" };

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const snap = await getDocs(query(collection(db, "deals"), orderBy("createdAt", "desc")));
        const data: Deal[] = snap.docs.map(d => ({
          id: d.id,
          business_name: d.data().businessName || d.data().business_name || "Unnamed",
          current_stage: d.data().currentStage || d.data().current_stage || "submission",
        }));
        setDeals(data);
        if (data.length > 0) setSelectedDealId(data[0].id);
      } catch (error) {
        console.error("Error fetching deals:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, []);

  const selectedDeal = deals.find(d => d.id === selectedDealId);

  return (
    <RequireStaff>
      <div className="space-y-6 font-['Geist',sans-serif]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <Badge variant="outline" className="text-amber-500 border-amber-500/50">
                <Shield className="h-3 w-3 mr-1" />{t.staffAccess}
              </Badge>
            </div>
            <p className="text-gray-400">{t.subtitle}</p>
          </div>
          {!loading && deals.length > 0 && (
            <Select value={selectedDealId} onValueChange={setSelectedDealId}>
              <SelectTrigger className="w-[300px] bg-gray-800 border-gray-700 text-white">
                <SelectValue placeholder={t.selectDeal} />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                {deals.map(deal => (
                  <SelectItem key={deal.id} value={deal.id} className="text-gray-300">
                    {deal.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {loading ? (
          <Skeleton className="h-64 w-full bg-gray-700" />
        ) : deals.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="py-12 text-center">
              <FolderOpen className="h-12 w-12 mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400">{t.noDeals}</p>
            </CardContent>
          </Card>
        ) : selectedDealId ? (
          <div className="bg-white rounded-xl p-6">
            <SecureDealBox dealId={selectedDealId} dealName={selectedDeal?.business_name} />
          </div>
        ) : null}
      </div>
    </RequireStaff>
  );
}
